import type {TransportAssocEntity} from "@ots-frontend/ots-data";
import {DataGridSeparator} from "../DataGrid/dataGridSeparators";

export const TICKET_SEPARATOR_OPTIONS = [
  {value: "NONE", label: "Off"},
  {value: "DRIVER", label: "Driver"},
  {value: "PICKUP_SITE", label: "Pickup Site"},
  {value: "DELIVERY_SITE", label: "Delivery Site"},
  {value: "PICKUP_COMPANY", label: "Pickup Company"},
  {value: "DELIVERY_COMPANY", label: "Delivery Company"},
  {value: "SCHEDULED_6_HOUR", label: "6 Hour Scheduled Block"},
  {value: "PICKUP_6_HOUR", label: "6 Hour Pickup Block"},
] as const;
export type TicketSeparatorMode = (typeof TICKET_SEPARATOR_OPTIONS)[number]["value"];

const identity = (id: string | undefined, name: string | undefined, fallback: string): DataGridSeparator => ({
  value: id !== undefined ? `id:${id}` : name !== undefined ? `name:${name}` : "MISSING",
  displayValue: name ?? fallback,
});

export function getTicketSeparator(
  ticket: TransportAssocEntity,
  mode: TicketSeparatorMode
): DataGridSeparator | undefined {
  const pickup = ticket.pickupTransport;
  const delivery = ticket.deliveryTransport;
  switch (mode) {
    case "NONE":
      return undefined;
    case "DRIVER": {
      const transport = pickup?.personId || pickup?.personName ? pickup : delivery;
      return identity(transport?.personId, transport?.personName, "Unassigned");
    }
    case "PICKUP_SITE":
      return identity(pickup?.siteId, pickup?.siteName, "No Pickup Site");
    case "DELIVERY_SITE":
      return identity(delivery?.siteId, delivery?.siteName, "No Delivery Site");
    case "PICKUP_COMPANY":
      return identity(pickup?.siteCompanyId, pickup?.siteCompanyName, "No Pickup Company");
    case "DELIVERY_COMPANY":
      return identity(delivery?.siteCompanyId, delivery?.siteCompanyName, "No Delivery Company");
    case "SCHEDULED_6_HOUR":
    case "PICKUP_6_HOUR": {
      const times = ticket.dispatches
        ?.map((dispatch) => dispatch.scheduleDttm)
        .filter((time): time is number => typeof time === "number" && Number.isFinite(time));
      const timestamp =
        mode === "PICKUP_6_HOUR"
          ? pickup?.createDttm ?? ticket.pickupDttm
          : times?.length
          ? Math.min(...times)
          : undefined;
      if (timestamp === undefined || !Number.isFinite(timestamp))
        return {value: "UNKNOWN", displayValue: mode === "PICKUP_6_HOUR" ? "No Pickup Time" : "Unscheduled"};
      const date = new Date(timestamp);
      if (!Number.isFinite(date.getTime())) return {value: "UNKNOWN", displayValue: "Unknown Time"};
      const hour = Math.floor(date.getHours() / 6) * 6;
      const day = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
        date.getDate()
      ).padStart(2, "0")}`;
      const value = `${day}T${String(hour).padStart(2, "0")}`;
      return {value, sortValue: value, displayValue: `${day} · ${hour}:00 - ${hour + 6}:00`};
    }
  }
}
